import { useState } from 'react';
import { RotterdamForm } from './rotterdam-form.jsx';
import { PESTS, STEDEN, cityForPostcode } from './data.js';
import { PestDrawing, IconPhone, IconMail, IconPin, IconCheck, IconShield, IconBolt, IconGraduation, IconWallet, IconBuilding, IconLeaf, IconCity, IconArrow, IconStar } from './icons.jsx';
import { url, navigate } from './url.js';
import { TrustLuxe } from './trust-luxe.jsx';
import { RatingBar } from './rating-bar.jsx';
import { PlaagCard } from './andere-diensten-carousel.jsx';

// ─── Hero ────────────────────────────────────────────────────────────
export function Hero() {
  const [pc, setPc] = useState("");
  // ── Hero form state ────────────────────────────────
  const [hf, setHf] = useState({ naam: "", telefoon: "", postcode: "", ongedierte: "" });
  const [hfSent, setHfSent] = useState(false);
  const updHf = k => e => setHf(f => ({ ...f, [k]: e.target.value }));

  const formatPc = (raw) => {
    let v = raw.replace(/\s/g, '').toUpperCase();
    if (v.length > 4) v = v.slice(0, 4) + ' ' + v.slice(4, 6);
    return v;
  };
  const onPcChange = (e) => setPc(formatPc(e.target.value));

  const searchPostcode = () => {
    const r = pc.trim().replace(/\s/g, '');
    if (r.length < 4) {
      alert('Vul een geldige postcode in, bijv. 2512 AB');
      return;
    }
    const city = cityForPostcode(r);
    if (city) {
      navigate(`/werkgebied/${city}?pc=${encodeURIComponent(r)}`);
    } else {
      navigate(`/`);
      setTimeout(() => {
        const el = document.getElementById('contact');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
        const f = document.querySelector('input[name="postcode"]');
        if (f) f.value = r.slice(0, 4) + ' ' + r.slice(4, 6);
      }, 50);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter') searchPostcode(); };

  const submitHero = (e) => {
    e.preventDefault();
    setHfSent(true);
  };

  return (
    <section className="hero">
      <div className="container">
        <div className="hero-inner">
          <div>
            <div className="hero-badge">
              <span className="hero-badge-bolt" aria-hidden="true">⚡</span>
              <span>Erkend &amp; gecertificeerd</span>
            </div>
            {/* SEO/semantiek fase 6: eyebrow uit H1 gehaald (was: span IN h1
                wat rendert als 'OngediertebestrijdingOngedierte weg?' zonder
                spatie). Nu: <p> boven H1, visueel identiek aan vroeger door
                .hero-h1-eyebrow styling + .hero-h1-slogan voor consistente
                Y-positie. Eén echte H1 over de hele pagina. */}
            <p className="hero-h1-eyebrow">
              Ongediertebestrijding in heel Noord-Holland, Zuid-Holland &amp; Utrecht
            </p>
            <h1 className="hero-h1-slogan">
              Last van ongedierte? <br/>
              <em>Wij lossen het op!</em>
            </h1>
            <p className="hero-sub">
              Voer je postcode in en ontvang direct hulp van een erkende ongediertebestrijder in heel Noord-Holland, Zuid-Holland en Utrecht. Spoedservice beschikbaar, ook 's avonds en in het weekend.
            </p>
            <div className="postcode-box">
              <div className="pc-wrap">
                <IconPin />
                <input
                  type="text" className="pc-input"
                  value={pc} onChange={onPcChange} onKeyDown={onKey}
                  placeholder="Jouw postcode, bijv. 2512 AB" maxLength={7} autoComplete="off"
                />
              </div>
              <button className="btn-zoek" onClick={searchPostcode}>
                Bekijk prijs &amp; beschikbaarheid →
              </button>
            </div>

            {/* Mobile-only rating-bar: direct onder de postcode-zoeker, voor
                de USP-vinkjes. Op desktop verborgen — daar staat de
                RatingBar lager (hero-rating-desktop). CSS-only switch. */}
            <div className="hero-rating-mobile">
              <RatingBar />
            </div>

            <div className="hero-trust">
              <div className="tit"><span className="tit-mark">✓</span> Vaste prijs vooraf</div>
              <div className="tit"><span className="tit-mark">✓</span> Particulier &amp; zakelijk</div>
              <div className="tit"><span className="tit-mark">✓</span> CTGB-gecertificeerd</div>
              <div className="tit"><span className="tit-mark">✓</span> Actief in NH, ZH &amp; Utrecht</div>
            </div>

            {/* Gedeelde RatingBar component, live count via Google Worker
                + clickable naar Google Maps. Vervangt het oude hero-rating
                blok dat hardcoded "150 Google reviews" toonde via CSS.
                Desktop-only: op mobile wordt de RatingBar bovenaan getoond
                (zie .hero-rating-mobile hierboven). */}
            <div className="hero-rating-desktop">
              <RatingBar />
            </div>
          </div>

          {/* Right side: compact offerte form. Mobile-audit fase 7:
              op <1024px CSS-hidden, want de postcode-bar boven dekt
              dezelfde lead-functie en de hero-stack wordt anders te lang.
              ContactSection lager op de pagina blijft beschikbaar. */}
          <div className="hero-form-desktop-only">
            <RotterdamForm variant="hero" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Stats ────────────────────────────────────────────────────────────
export function Stats() {
  return (
    <div className="stats stats-luxe">
      <div className="container">
        <TrustLuxe />
      </div>
    </div>
  );
}

// ─── Pest grid ────────────────────────────────────────────────────────
export function Plagen() {
  // Mobiel (≤640px): toon alleen de 3 meest gevraagde plagen + link naar de
  // rest, zodat bezoekers niet door 15 kaarten hoeven te scrollen.
  const mobielePlagen = ['wespen', 'muizen', 'ratten']
    .map(s => PESTS.find(p => p.slug === s))
    .filter(Boolean);
  return (
    <section className="section" id="ongedierte">
      <div className="container">
        <div className="sh">
          <div className="section-tag">Ongediertebestrijding</div>
          <h2>Welk ongedierte bestrijden <em>wij?</em></h2>
          <p>Professioneel, snel en vakkundig, voor elk soort ongedierte hebben wij de juiste aanpak.</p>
        </div>
        {/* Editorial card-grid — zelfde stijl als de slider op detail-
            pagina's. Single source PlaagCard component. */}
        <div className="adc-grid plagen-full">
          {PESTS.map(p => (
            <PlaagCard key={p.slug} plaag={p} href={url.plaag(p.slug)} />
          ))}
        </div>
        {/* Mobiel-only: 3 hoofdplagen + link naar alle diensten. */}
        <div className="plagen-mobile">
          {mobielePlagen.map(p => (
            <PlaagCard key={p.slug} plaag={p} href={url.plaag(p.slug)} />
          ))}
          <a href="/diensten" className="plagen-mobile-link">Bekijk alle ongedierte →</a>
        </div>

        <div className="spoed-strip fi">
          <div>
            <div className="spoed-text">🚨 Spoed? Wij zijn ook <em>'s avonds en in het weekend</em> bereikbaar!</div>
            <div className="spoed-text-sub">Binnen 24 uur een reactie, vakkundige inspectie bij jou thuis</div>
          </div>
          <div className="spoed-actions">
            <a href="tel:0855005050" className="btn-bel-lg"><IconPhone width="16" height="16" /> 085 500 50 50</a>
            <a href={url.offerte()} className="btn-form-lg">✉ Offerte aanvragen</a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Werkwijze ────────────────────────────────────────────────────────
export function Werkwijze() {
  const steps = [
    { n: 1, t: "Inspectie ter plaatse",   d: "Onze specialist bezoekt je locatie en brengt de situatie volledig in kaart.", img: "/assets/inspecteur-stap-1.png" },
    { n: 2, t: "Aanpak bepalen",     d: "Persoonlijk behandelplan dat precies past bij jouw situatie en het type ongedierte.", img: "/assets/inspecteur-stap-2.png" },
    { n: 3, t: "Duidelijke offerte", d: "Heldere offerte zonder verborgen kosten. Geen verrassingen achteraf.", img: "/assets/inspecteur-stap-3.png" },
    { n: 4, t: "Direct aan de slag", d: "Na akkoord starten we meteen met een vakkundige, grondige aanpak.", img: "/assets/inspecteur-stap-4.png" },
  ];
  return (
    <section className="section section-bg" id="werkwijze">
      <div className="container">
        <div className="sh">
          <div className="section-tag">Onze werkwijze</div>
          <h2>In 4 stappen <em>ongediertevrij</em></h2>
          <p>Eenvoudig, transparant en effectief, zo werken wij elke dag.</p>
        </div>
        <div className="process-grid">
          {steps.map((s, i) => (
            <div key={s.n} className={`ps fi fi-d${i + 1}`}>
              <div className="ps-inspector">
                {s.img ? (
                  <picture>
                    <source srcSet={s.img.replace(/\.png$/, '.webp')} type="image/webp" />
                    <img
                      src={s.img}
                      alt={`Inspecteur stap ${s.n}: ${s.t}`}
                      className="ps-inspector-img"
                      loading="lazy"
                      decoding="async"
                      width="220"
                      height="260"
                    />
                  </picture>
                ) : (
                  <div className="ps-inspector-placeholder" aria-hidden="true"></div>
                )}
              </div>
              <h3>{s.t}</h3>
              <p>{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── USPs ────────────────────────────────────────────────────────────
export function USPs() {
  const usps = [
    { tone: "orange", t: "Spoeddienst 24/7", d: "Ook 's avonds en in het weekend. Ongedierte wacht niet, wij ook niet.",
      icon: (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="24" cy="24" r="22" fill="#FB923C"/>
          <polygon points="22,8 12,28 22,28 20,40 34,20 24,20 26,8" fill="white"/>
        </svg>
      ) },
    { tone: "blue", t: "Gecertificeerde specialisten", d: "Vakkundig opgeleid en gecertificeerde methoden voor elk soort ongedierte.",
      icon: (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polygon points="6,18 24,8 42,18 24,28" fill="#3B82F6"/>
          <rect x="14" y="22" width="20" height="14" rx="2" fill="#1B2A4A"/>
          <line x1="42" y1="18" x2="42" y2="32" stroke="#3B82F6" strokeWidth="2.4" strokeLinecap="round"/>
          <circle cx="42" cy="34" r="2.4" fill="#FBBF24"/>
        </svg>
      ) },
    { tone: "green", t: "Resultaat en zekerheid", d: "Geen resultaat? Dan komen we gratis terug voor een vervolgbehandeling.",
      icon: (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M24 4 L40 11 V24 C40 33 32 40 24 44 C16 40 8 33 8 24 V11 Z" fill="#22C55E"/>
          <path d="M16 24 L22 30 L33 18" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      ) },
    { tone: "yellow", t: "Transparante prijzen", d: "Vaste tarieven, geen verborgen kosten. Je weet exact wat je betaalt.",
      icon: (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="14" width="36" height="22" rx="3" fill="#FBBF24"/>
          <circle cx="24" cy="25" r="6" fill="white"/>
          <text x="24" y="29" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontWeight="900" fontSize="10" fill="#92400E">€</text>
          <circle cx="12" cy="20" r="1.5" fill="white"/>
          <circle cx="36" cy="30" r="1.5" fill="white"/>
        </svg>
      ) },
    { tone: "purple", t: "Particulier & zakelijk", d: "Woning, horeca, kantoor of bedrijfspand, wij hebben de juiste oplossing.",
      icon: (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 28 L14 18 L22 28 L22 42 L6 42 Z" fill="#A78BFA"/>
          <rect x="10" y="32" width="3" height="4" fill="white"/>
          <rect x="15" y="32" width="3" height="4" fill="white"/>
          <rect x="24" y="14" width="18" height="28" rx="1.5" fill="#7C3AED"/>
          <rect x="27" y="18" width="3" height="3" fill="white"/>
          <rect x="33" y="18" width="3" height="3" fill="white"/>
          <rect x="27" y="24" width="3" height="3" fill="white"/>
          <rect x="33" y="24" width="3" height="3" fill="white"/>
          <rect x="27" y="30" width="3" height="3" fill="white"/>
          <rect x="33" y="30" width="3" height="3" fill="white"/>
        </svg>
      ) },
    { tone: "leaf", t: "Milieuvriendelijk", d: "Effectief voor ongedierte, veilig voor mensen, huisdieren en het milieu.",
      icon: (
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M40 8 C40 8 20 6 12 20 C4 34 16 42 16 42 C16 42 36 40 40 24 C42 16 40 8 40 8 Z" fill="#16A34A"/>
          <path d="M14 38 L36 14" stroke="white" strokeWidth="1.8" strokeLinecap="round" opacity=".7"/>
        </svg>
      ) },
  ];
  return (
    <section className="section">
      <div className="container">
        <div className="sh">
          <div className="section-tag">Waarom ABC Ongedierte?</div>
          <h2>Wij doen het <em>anders</em></h2>
        </div>
        <div className="usp-grid spoed-grid">
          {usps.map((u, i) => (
            <div key={u.t} className={`usp-card spoed-card spoed-tone-${u.tone} fi fi-d${(i % 6) + 1}`}>
              <div className="usp-icon spoed-icon">{u.icon}</div>
              <div>
                <h3>{u.t}</h3>
                <p>{u.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

